// ProductList.vue
<template>
    <div class="max-w-3xl mx-auto">
        <ProgressBar :value="progressValue"
            :class="allProductsPrepared ? 'p-progressbar-success' : 'p-progressbar-info'">
            {{ progressValue }}%
        </ProgressBar>

        <div class="mt-4">
            <h5>Détails de la tournée : {{ tourDetails.tournee_name }}</h5>
            <Listbox v-model="selectedProducts" :options="tourDetails.products" optionLabel="name"
                optionValue="name" class="w-full mt-2" multiple :class="{ 'p-listbox-highlight-selected': true }"
                @change="handleProductSelection">
                <template #option="slotProps">
                    <div :class="['product-item', { 'selected': isProductSelected(slotProps.option) }]"
                        @click.stop="toggleProductPreparation(slotProps.option)">
                        <Tag :value="slotProps.option.quantity"
                            :severity="slotProps.option.prepared ? 'success' : 'warning'"
                            class="custom-quantity-badge" />
                        <span class="product-name">{{ slotProps.option.name }}</span>
                    </div>
                </template>
            </Listbox>
        </div>

        <div class="mt-4">
            <Button 
                label="Suivant" 
                icon="pi pi-arrow-right" 
                iconPos="right"
                :disabled="!allProductsPrepared"
                @click="$emit('next-step')"
                class="w-full"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    tourDetails: {
        type: Object,
        required: true
    }
});

defineEmits(['next-step']);

const selectedProducts = ref([]);

const allProductsPrepared = computed(() => {
    return props.tourDetails?.products.every(product => product.prepared) ?? false;
});

const progressValue = computed(() => {
    if (!props.tourDetails?.products) return 0;
    const totalProducts = props.tourDetails.products.length;
    const preparedCount = props.tourDetails.products.filter(p => p.prepared).length;
    return Math.round((preparedCount / totalProducts) * 100);
});

const handleProductSelection = (event) => {
    selectedProducts.value = event.value;
    updateProductsPreparedStatus();
};

const toggleProductPreparation = (product) => {
    product.prepared = !product.prepared;
    
    // Mettre à jour selectedProducts en fonction de l'état prepared
    if (product.prepared) {
        // Ajouter le produit à la sélection s'il n'y est pas déjà
        if (!selectedProducts.value.includes(product.name)) {
            selectedProducts.value.push(product.name);
        }
    } else {
        // Retirer le produit de la sélection
        selectedProducts.value = selectedProducts.value.filter(name => name !== product.name);
    }
};

const updateProductsPreparedStatus = () => {
    props.tourDetails.products.forEach(product => {
        product.prepared = selectedProducts.value.includes(product.name);
    });
};

const isProductSelected = (product) => {
    return selectedProducts.value.includes(product.name);
};
</script>


<style scoped>
.w-full {
    width: 100%;
}

.p-mb-3 {
    margin-bottom: 1rem;
}

.p-mt-4 {
    margin-top: 1.5rem;
}

/* Styles pour la Dropdown */
.custom-dropdown :deep(.p-dropdown) {
    font-size: 1.2rem;
    /* Taille de base pour tout le composant */
    font-weight: bold;
    /* Gras pour le label */
}

.custom-dropdown :deep(.p-dropdown-label),
.custom-dropdown :deep(.p-dropdown-item) {
    padding: 0.75rem 1rem;
    font-size: 1.2rem;
    /* Même taille pour le label et les items */
    font-weight: bold;
    /* Gras pour le label et les items */
}

.custom-dropdown :deep(.p-dropdown-trigger) {
    padding: 0 1rem;
    display: flex;
    align-items: center;
    font-weight: bold;
    /* Gras pour le label */
}

.custom-dropdown :deep(.p-dropdown-panel) {
    font-size: 1.2rem;
    /* Assure que le panel utilise la même taille de police */
}

.custom-dropdown :deep(.p-dropdown-items) {
    padding: 0.5rem 0;
    /* Ajoute un peu d'espace en haut et en bas de la liste */
}

.custom-dropdown :deep(.p-dropdown-item) {
    margin: 0;
    /* Réinitialise les marges pour les items */
}

/* Autres styles existants */
.custom-quantity-badge {
    width: 2.5em;
    height: 2.5em;
    font-size: 1.2em;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.5em;
    transition: all 0.3s ease;
    border-radius: 8px;
    padding: 0;
}

.product-item.selected .custom-quantity-badge {
    border-radius: 50%;
}

.product-name {
    font-size: 1.2rem;
    font-weight: bold;
}

.upload-buttons-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-top: 1.5rem;
}

.product-item {
    cursor: pointer;
    transition: background-color 0.3s;
    display: flex;
    align-items: center;
}
</style>